import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody } from './ui/Panel';
import Pill from './ui/Pill';
import { ErrorNotice, SkeletonRows } from './ui/States';
import { PARTICIPANT_TONE, label } from '../lib/programs';
import { COMMITTEE_ROLE_LABEL } from '../lib/committees';

interface RoleRow {
  participantId: string;
  cycleId: string;
  cycle: string;
  programKey: string;
  program: string;
  role: string;
  status: string;
  perspective: string | null;
  meetingsAttended: number;
}

/** Committee seats of a member or a contact. */
export function CommitteeRolesList({ userId, contactId }: { userId?: string; contactId?: string }) {
  const list = useQuery<{ restricted: boolean; roles: RoleRow[] }>(
    ['committee-roles', userId, contactId],
    async () => (await api.get('/committees/roles', { params: { userId, contactId } })).data.data,
  );
  if (list.isLoading) return <SkeletonRows rows={2} />;
  if (list.error) return <ErrorNotice>Could not load committee roles.</ErrorNotice>;
  if (list.data?.restricted)
    return (
      <p className="text-small text-ink-2">Restricted: only admins see the committee seats of a member under 18.</p>
    );
  if (!list.data?.roles.length) return <p className="text-small text-ink-2">Not on any committee.</p>;
  return (
    <ul className="text-table">
      {list.data.roles.map((r) => (
        <li
          key={r.participantId}
          className="flex items-center justify-between gap-3 py-2 border-b border-line last:border-0"
        >
          <div>
            <div>
              {r.program} {r.cycle}
            </div>
            <div className="text-small text-ink-2">
              {COMMITTEE_ROLE_LABEL[r.role] ?? label(r.role)}
              {r.perspective ? ` · ${label(r.perspective)}` : ''} · {r.meetingsAttended} meeting
              {r.meetingsAttended === 1 ? '' : 's'} attended
            </div>
          </div>
          <Pill tone={PARTICIPANT_TONE[r.status] ?? 'planned'}>{label(r.status)}</Pill>
        </li>
      ))}
    </ul>
  );
}

export function MemberCommittees({ userId }: { userId: string }) {
  return (
    <Panel>
      <PanelHeader title="Committees" />
      <PanelBody className="pt-2">
        <CommitteeRolesList userId={userId} />
        <Link className="text-small underline block mt-3" to="/committees">
          Committees
        </Link>
      </PanelBody>
    </Panel>
  );
}
